import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, MoreHorizontal } from 'lucide-react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { useClinic } from '@/hooks/useClinicSettings';
import { useUnreadPatientMessageCount } from '@/hooks/usePatientMessages';
import { useUnreadUnmatchedEmailCount } from '@/hooks/usePatientEmailThreads';
import { useMyOpenTaskCount } from '@/hooks/useStaffTasks';
import { ClinicSelector } from '@/components/ClinicSelector';
import { Toggle } from '@/components/ui/Toggle';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';
import { readActivatedModules } from '@/lib/clinicModules';
import {
  isNavItemActive,
  mobileMoreNavItems,
  mobilePrimaryNavItems,
  navBadgeCount,
  visibleClinicNavItems,
} from '@/components/Layout/sidebarNav';
import { UnreadBadge } from '@/components/ui/UnreadBadge';
import { NAV_ICONS } from '@/components/Layout/navIcons';
import { UserAccountCard } from '@/components/Layout/UserAccountCard';
import { StaffNotificationBell } from '@/components/Layout/StaffNotificationBell';

export function MobileBottomBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isPlatformAdmin, hasPermission, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { activeClinic } = useActiveClinic();
  const activeClinicSettings = useClinic(activeClinic?.id ?? '');
  const [moreOpen, setMoreOpen] = useState(false);
  const canViewMessages = Boolean(
    activeClinic?.id && hasPermission(activeClinic.id, 'messages', 'view'),
  );
  const unreadMessages = useUnreadPatientMessageCount(
    canViewMessages ? (activeClinic?.id ?? null) : null,
  );
  const canViewUnmatchedEmail = Boolean(
    activeClinic?.id && hasPermission(activeClinic.id, 'communication', 'view'),
  );
  const unreadUnmatchedEmails = useUnreadUnmatchedEmailCount(
    canViewUnmatchedEmail ? (activeClinic?.id ?? null) : null,
    canViewUnmatchedEmail ? (user?.id ?? null) : null,
  );
  const canViewTasks = Boolean(
    activeClinic?.id && hasPermission(activeClinic.id, 'tasks', 'view'),
  );
  const openTasks = useMyOpenTaskCount(
    canViewTasks ? (activeClinic?.id ?? null) : null,
    canViewTasks ? (user?.id ?? null) : null,
  );

  const visible = visibleClinicNavItems({
    isPlatformAdmin,
    clinicId: activeClinic?.id,
    hasPermission,
    activatedModules: readActivatedModules(activeClinicSettings.data?.activated_modules),
    modulesLoaded: activeClinicSettings.isSuccess,
  });
  const primary = mobilePrimaryNavItems(visible);
  const more = mobileMoreNavItems(visible);
  const unreadCount = unreadMessages.data ?? 0;
  const openTaskCount = openTasks.data ?? 0;
  const unmatchedEmailCount = unreadUnmatchedEmails.data ?? 0;
  const badgeCounts = {
    unreadMessages: unreadCount,
    openTasks: openTaskCount,
    unreadUnmatchedEmails: unmatchedEmailCount,
  };
  const moreActive = more.some((item) => isNavItemActive(item.to, location.pathname));

  const handleSignOut = async () => {
    setMoreOpen(false);
    try {
      await signOut();
    } finally {
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-20 flex border-t border-ds-line bg-ds-bar-bg px-s1 pb-[calc(8px+env(safe-area-inset-bottom))] pt-s2 shadow-[0_-4px_16px_rgba(0,0,0,.04)] nav:hidden"
        aria-label={t('design.main_nav')}
      >
        {primary.map((item) => {
          const Icon = NAV_ICONS[item.icon];
          const active = isNavItemActive(item.to, location.pathname);
          const unread = navBadgeCount(item.badge, badgeCounts);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-ctl px-0 py-0.5 text-[11px] leading-4',
                active ? 'font-semibold text-ds-teal' : 'font-medium text-ds-muted',
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-ctl',
                  active && 'bg-ds-teal-tint',
                )}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="truncate">{t(item.i18nKey)}</span>
              {unread > 0 ? (
                <span className="absolute right-[calc(50%-18px)] top-0 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-ds-coral-badge px-1 text-[10px] font-semibold text-white tabular-nums">
                  {unread}
                </span>
              ) : null}
            </NavLink>
          );
        })}
        {more.length > 0 ? (
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            className={cn(
              'flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-ctl text-[11px] leading-4',
              moreActive ? 'font-semibold text-ds-teal' : 'font-medium text-ds-muted',
            )}
          >
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-ctl',
                moreActive && 'bg-ds-teal-tint',
              )}
            >
              <MoreHorizontal className="h-5 w-5" />
            </span>
            {t('nav.more')}
          </button>
        ) : null}
      </nav>

      <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} className="relative z-50 nav:hidden">
        <DialogBackdrop className="fixed inset-0 bg-ds-scrim" />
        <div className="fixed inset-x-0 bottom-0">
          <DialogPanel className="rounded-t-card bg-ds-card px-s4 pb-[calc(16px+env(safe-area-inset-bottom))] pt-s3 shadow-pop">
            <div className="mx-auto mb-s3 h-1 w-10 rounded-full bg-ds-line" aria-hidden />
            <p className={cn(TYPE.heading, 'mb-s3 text-ds-text')}>{t('nav.more')}</p>
            <ClinicSelector alwaysShow />
            <ul className="mt-s3 flex flex-col gap-0.5">
              {more.map((item) => {
                const Icon = NAV_ICONS[item.icon];
                const active = isNavItemActive(item.to, location.pathname);
                const badge = navBadgeCount(item.badge, badgeCounts);
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className={cn(
                        TYPE.control,
                        'flex h-10 items-center gap-s3 rounded-ctl px-s3',
                        active ? 'bg-ds-teal-tint font-semibold text-ds-teal' : 'text-ds-text',
                      )}
                    >
                      <Icon className="h-5 w-5 text-ds-muted" />
                      <span className="min-w-0 flex-1 truncate">{t(item.i18nKey)}</span>
                      <UnreadBadge count={badge} />
                    </NavLink>
                  </li>
                );
              })}
            </ul>
            <div className="mt-s3">
              <StaffNotificationBell />
              <div className="mt-s1">
                <UserAccountCard onNavigate={() => setMoreOpen(false)} />
              </div>
            </div>
            <div className="mt-s3 border-t border-ds-line pt-s3">
              <div className="flex h-10 items-center gap-s3 px-s3">
                <Toggle
                  track="switch"
                  checked={theme === 'dark'}
                  onCheckedChange={() => toggleTheme()}
                  aria-label={t('nav.toggle_theme_dark')}
                />
                <span className={cn(TYPE.control)}>{t('nav.toggle_theme_dark')}</span>
              </div>
              <button
                type="button"
                onClick={handleSignOut}
                className={cn(TYPE.control, 'flex h-10 w-full items-center gap-s3 rounded-ctl px-s3 text-ds-text')}
              >
                <LogOut className="h-5 w-5 text-ds-muted" />
                {t('nav.sign_out')}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
